import { useCallback, useEffect, useState } from "react";
import { Mail, Plus, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import ReglagesMCP from "@/components/ReglagesMCP";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EmailList = ({ titre, aide, emails, setEmails, testid }) => {
  const [saisie, setSaisie] = useState("");

  const ajouter = () => {
    const email = saisie.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Adresse email invalide");
    if (emails.includes(email)) return toast.error("Cette adresse est déjà dans la liste");
    setEmails([...emails, email]);
    setSaisie("");
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div>
        <div className="bk-label flex items-center gap-2">
          <Mail size={14} /> {titre}
        </div>
        <p className="text-sm text-slate-600 mt-1.5">{aide}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {emails.map((email) => (
          <span key={email} data-testid={`${testid}-tag-${email}`} className="flex items-center gap-2 text-sm bg-slate-100 text-slate-800 rounded-full pl-3 pr-2 py-1.5">
            {email}
            <button data-testid={`${testid}-remove-${email}`} onClick={() => setEmails(emails.filter((e) => e !== email))} className="text-slate-400 hover:text-red-500">
              <X size={14} />
            </button>
          </span>
        ))}
        {emails.length === 0 && <span className="text-sm text-slate-400">Aucun destinataire — aucune notification email ne sera envoyée.</span>}
      </div>
      <div className="flex gap-2">
        <Input
          data-testid={`${testid}-input`}
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), ajouter())}
          placeholder="prenom.nom@bkpro.fr"
          className="h-11"
        />
        <Button data-testid={`${testid}-add-btn`} onClick={ajouter} variant="outline" className="h-11">
          <Plus size={15} />
        </Button>
      </div>
    </div>
  );
};

export default function AdminSettings() {
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [devisEmails, setDevisEmails] = useState([]);
  const [ticketEmails, setTicketEmails] = useState([]);
  const [info, setInfo] = useState({});
  const [axonaut, setAxonaut] = useState({});
  const [logs, setLogs] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const appliquer = (data) => {
    setUrl(data.bkflow_webhook_url || "");
    setDevisEmails(data.devis_emails || []);
    setTicketEmails(data.ticket_emails || []);
    setInfo(data);
  };

  const load = useCallback(() => {
    api.get("/admin/settings").then(({ data }) => appliquer(data));
    api.get("/admin/axonaut/status").then(({ data }) => setAxonaut(data));
    api.get("/admin/webhook-logs").then(({ data }) => setLogs(data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", {
        bkflow_webhook_url: url,
        bkflow_webhook_secret: secret,
        devis_emails: devisEmails,
        ticket_emails: ticketEmails,
      });
      appliquer(data);
      setSecret("");
      toast.success("Réglages enregistrés");
    } catch (e) {
      toast.error(errMsg(e, "Enregistrement impossible"));
    } finally {
      setBusy(false);
    }
  };

  const tester = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/webhook/test");
      setTestResult(data);
      toast[data.ok ? "success" : "error"](data.ok ? "BKFlow a bien reçu le ping" : `BKFlow inaccessible : ${data.status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Test impossible"));
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/axonaut/sync");
      toast.success(`Axonaut synchronisé : ${data.companies} sociétés, ${data.employees} contacts, ${data.contracts} contrats`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Synchronisation impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-settings">
      <div>
        <div className="bk-label mb-2">Intégrations</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Réglages & webhooks</h1>
      </div>

      <ReglagesMCP />

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-6" data-testid="notif-emails-panel">
        <div>
          <div className="bk-label">Destinataires des notifications</div>
          <p className="text-sm text-slate-600 mt-1.5">
            Les alertes WhatsApp CallMeBot restent envoyées en parallèle aux membres de l'équipe qui l'ont activé dans leur fiche utilisateur.
          </p>
        </div>
        <EmailList
          titre="Demandes de devis (vitrine)"
          aide="Chaque clic sur « Demander un devis similaire » envoie un email à ces adresses."
          emails={devisEmails}
          setEmails={setDevisEmails}
          testid="devis-emails"
        />
        <div className="h-px bg-slate-100" />
        <EmailList
          titre="Nouveaux tickets SAV"
          aide="Chaque nouveau ticket non auto-résolu est notifié à ces adresses."
          emails={ticketEmails}
          setEmails={setTicketEmails}
          testid="ticket-emails"
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="bk-label">Webhook sortant BKFlow (onglet Alertes — tab SAV)</div>
        <p className="text-sm text-slate-600">
          Chaque ticket est envoyé en POST JSON avec l'en-tête <code className="font-mono-bk">X-Webhook-Secret</code>. Le champ{" "}
          <code className="font-mono-bk">external_id</code> reprend la référence BKPROD : un renvoi met à jour le ticket au lieu de créer un doublon.
          Urgence → priorité : bloquant = <strong>critical</strong>, gênant = high, mineur = normal.
        </p>
        <Input data-testid="webhook-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://bkpro.app/api/sav-tickets/webhook" className="h-12 font-mono-bk text-xs" />
        <Input
          data-testid="webhook-secret-input"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder={info.bkflow_webhook_secret_defini ? `Secret configuré (${info.bkflow_webhook_secret_apercu}) — saisir pour remplacer` : "X-Webhook-Secret"}
          className="h-12 font-mono-bk text-xs"
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="webhook-test-btn" onClick={tester} disabled={busy} variant="outline">
            Tester la connexion BKFlow
          </Button>
          {testResult && (
            <span data-testid="webhook-test-result" className={`text-xs ${testResult.ok ? "text-emerald-600" : "text-red-600"}`}>
              {testResult.ok ? "BKFlow a répondu : " : "Échec : "}
              <span className="font-mono-bk">{testResult.status}</span>
            </span>
          )}
        </div>
      </div>

      <Button data-testid="webhook-save-btn" onClick={save} disabled={busy} className="bg-[#005CFF] hover:bg-[#0047CC] h-12 px-6">
        Enregistrer les réglages
      </Button>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="axonaut-panel">
        <div className="bk-label">Axonaut (source de vérité contacts / contrats)</div>
        <div className="text-sm text-slate-600">
          {axonaut.actif ? (
            <>
              Clé API active. Dernière synchronisation : <strong>{axonaut.last_sync ? fmtDate(axonaut.last_sync) : "jamais"}</strong>
              {axonaut.counts?.companies ? ` — ${axonaut.counts.companies} sociétés, ${axonaut.counts.employees} contacts, ${axonaut.counts.contracts} contrats.` : ""}
            </>
          ) : (
            "Clé API absente : les contacts de démonstration sont utilisés."
          )}
        </div>
        <Button data-testid="axonaut-sync-btn" onClick={sync} disabled={busy || !axonaut.actif} variant="outline">
          <RefreshCw size={15} className={`mr-2 ${busy ? "animate-spin" : ""}`} /> Synchroniser maintenant
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="bk-label mb-3">Endpoint entrant de test</div>
        <p className="text-sm text-slate-600">
          <code className="font-mono-bk text-xs">POST /api/bkflow-test/alerts</code> — reçoit et journalise les payloads, utile pour rejouer une intégration n8n.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="webhook-logs">
        <div className="bk-label mb-3">Derniers envois ({logs.length})</div>
        <div className="space-y-2">
          {logs.map((l) => (
            <div key={l.id} className="text-sm flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-2">
              <span className="font-mono-bk text-xs">{l.ticket_reference}</span>
              <span className={l.status?.startsWith("envoye_2") ? "text-emerald-600" : "text-amber-600"}>{l.status}</span>
              <span className="text-xs text-slate-500">{fmtDate(l.created_at)}</span>
            </div>
          ))}
          {logs.length === 0 && <p className="text-sm text-slate-500">Aucun envoi.</p>}
        </div>
      </div>
    </div>
  );
}
